import type { Lang } from "../types/i18n";

type Dict = Record<string, string>;

// Keep keys stable. If a key is missing in the selected language, the English value is used.
export const translations: Record<Lang, Dict> = {
  en: {
    "lang.en": "EN",
    "lang.de": "DE",

    // Footer
    "footer.rights": "takeKONTROL © 2024 · All rights reserved",

    // Welcome
    "welcome.message": "powered by takeKONTROL",
    "welcome.start": "Press here to start",

    // Nav
    "nav.kontrolScore": "Readiness Level",
    "nav.actionPlan": "Action Plan",
    "nav.managerView": "Manager Dashboard",
    "nav.userRights": "User Rights",
    "nav.companies": "Companies",
    "nav.events": "Events",
    "nav.feedback": "Feedback",
    "nav.profile": "Profile",
    "nav.emergency": "Emergency Contacts",
    "nav.loading": "Loading…",
    "nav.menu": "Menu",
    "nav.help": "Help",
    "nav.switchAccount": "Switch account",

    // Entry
    "entry.brand": "takeKONTROL",
    "entry.email.title": "Enter your company email",
    "entry.email.placeholder": "example@company.com",
    "entry.email.continue": "Continue",
    "entry.email.register": "Register yourself",

    "entry.password.login.title": "Enter your password",
    "entry.password.login.cta": "Log in",
    "entry.password.forgot": "Forgot your password?",
    "entry.password.useDifferent": "Use a different email",
    "entry.password.placeholder": "Password",

    "entry.password.set.title": "Set your password",
    "entry.password.set.cta": "Create account",
    "entry.back": "Back",

    "entry.register.title": "Create your account",
    "entry.register.subtitle": "Register with your company details to continue.",
    "entry.register.cta": "Create account",
    "entry.register.companyPlaceholder": "Company name",
    "entry.register.companySuggestionPrompt": "Did you mean one of these?",
    "entry.register.companySuggestionConfirm": "Yes, that's us",
    "entry.register.companySuggestionReject": "No, different company",
    "entry.register.companyUseAsNew": "None of these — use \"{name}\" as a new company",
    "entry.register.companyMatchedNote": "Registering under {name}.",
    "entry.register.companyNewNote": "Will register \"{name}\" as a new company.",
    "entry.register.companyChange": "Change",
    "entry.register.newCompanyDialogTitle": "Add your company",
    "entry.register.newCompanyDialogNameLabel": "Registered company name",
    "entry.register.newCompanyDialogAliasLabel": "Short form / alias (optional)",
    "entry.register.newCompanyDialogAliasHelper": "e.g. an abbreviation your team uses",
    "entry.register.newCompanyDialogSave": "Save and continue",
    "entry.register.newCompanyDialogCancel": "Cancel",
    "entry.register.departmentPlaceholder": "Department",
    "entry.register.anonymousToggle": "Participate anonymously",
    "entry.register.anonymousHelper": "Your email is only used to sign in and confirm your account. We do not share it with your company.",
    "entry.register.firstName": "First name",
    "entry.register.lastName": "Last name",
    "entry.register.positionPlaceholder": "Position",
    "entry.register.personalAddress": "Personal address",
    "entry.register.loginPrompt": "Already have an account?",
    "entry.register.loginLink": "Already have an account? Log in here",
    "entry.register.loginCta": "Log in here",

    "entry.reset.title": "Reset password",
    "entry.reset.subtitle": "Enter your email and we'll send you a reset link.",
    "entry.reset.cta": "Send reset link",
    "entry.reset.backToLogin": "Back to login",

    "entry.confirm.title": "Confirm your email",
    "entry.confirm.subtitle": "We sent you a confirmation link. Please check your inbox and confirm your email address before logging in.",
    "entry.confirm.backToLogin": "Back to login",
    "entry.confirm.resend": "Resend confirmation",

    "entry.loading": "Loading…",

    // Errors / info
    "entry.err.invalidEmail": "Please enter a valid company email.",
    "entry.err.invalidEmailDomain": "We couldn't verify that email's domain. Please double-check it for typos.",
    "entry.err.notFound": "Email not found in company records.",
    "entry.err.edgeInvoke": "Failed to send a request to the Edge Function.",
    "entry.err.noPassword": "Please enter your password.",
    "entry.err.linkFailed": "Login worked, but linking failed.",
    "entry.err.pwTooShort": "Password must be at least 8 characters.",
    "entry.err.companyRequired": "Please enter your company name.",
    "entry.err.companyUnconfirmed": "Please confirm your company before continuing.",
    "entry.err.companyNameRequired": "Please enter the company's registered name.",
    "entry.err.departmentRequired": "Please choose your department.",
    "entry.err.nameRequired": "Please enter your first and last name.",
    "entry.err.userExists": "This email already exists. Please log in instead.",
    "entry.err.selfRegisterFailed": "We couldn't create your account. Please try again.",
    "entry.alert.userExists": "This email already exists. Please log in instead.",
    "entry.alert.checkEmail": "Please check your email to confirm your account, then log in.",
    "entry.info.checkEmailConfirm": "Check your email to confirm your account, then log in.",
    "entry.reset.invalidEmail": "Please enter a valid email.",
    "entry.reset.sent": "If this email exists, a reset link was sent. Please check your inbox.",
  },
  de: {
    "lang.en": "EN",
    "lang.de": "DE",

    // Footer
    "footer.rights": "takeKONTROL © 2024 · Alle Rechte vorbehalten",

    // Welcome
    "welcome.message": "powered by takeKONTROL",
    "welcome.start": "Hier drücken, um zu starten",

    // Nav
    "nav.kontrolScore": "Bereitschaftsgrad",
    "nav.actionPlan": "Aktionsplan",
    "nav.managerView": "Manager-Ansicht",
    "nav.userRights": "Nutzerrechte",
    "nav.companies": "Unternehmen",
    "nav.events": "Anlässe",
    "nav.feedback": "Feedback",
    "nav.profile": "Profil",
    "nav.emergency": "Notfall Kontakte",
    "nav.loading": "Lädt…",
    "nav.menu": "Menü",
    "nav.help": "Hilfe",
    "nav.switchAccount": "Konto wechseln",

    // Entry
    "entry.brand": "takeKONTROL",
    "entry.email.title": "Gib deine Firmen-E-Mail ein",
    "entry.email.placeholder": "Firmen-E-Mail",
    "entry.email.continue": "Weiter",
    "entry.email.register": "Selbst registrieren",

    "entry.password.login.title": "Gib dein Passwort ein",
    "entry.password.login.cta": "Anmelden",
    "entry.password.forgot": "Passwort vergessen?",
    "entry.password.useDifferent": "Andere E-Mail verwenden",
    "entry.password.placeholder": "Passwort",

    "entry.password.set.title": "Passwort festlegen",
    "entry.password.set.cta": "Konto erstellen",
    "entry.back": "Zurück",

    "entry.register.title": "Konto erstellen",
    "entry.register.subtitle": "Registriere dich mit deinen Firmendaten, um fortzufahren.",
    "entry.register.cta": "Konto erstellen",
    "entry.register.companyPlaceholder": "Firmenname",
    "entry.register.companySuggestionPrompt": "Meinst du eines davon?",
    "entry.register.companySuggestionConfirm": "Ja, das sind wir",
    "entry.register.companySuggestionReject": "Nein, anderes Unternehmen",
    "entry.register.companyUseAsNew": "Keines davon — \"{name}\" als neues Unternehmen anlegen",
    "entry.register.companyMatchedNote": "Registrierung bei {name}.",
    "entry.register.companyNewNote": "\"{name}\" wird als neues Unternehmen angelegt.",
    "entry.register.companyChange": "Ändern",
    "entry.register.newCompanyDialogTitle": "Unternehmen hinzufügen",
    "entry.register.newCompanyDialogNameLabel": "Eingetragener Firmenname",
    "entry.register.newCompanyDialogAliasLabel": "Kurzform / Alias (optional)",
    "entry.register.newCompanyDialogAliasHelper": "z. B. eine Abkürzung, die dein Team verwendet",
    "entry.register.newCompanyDialogSave": "Speichern und fortfahren",
    "entry.register.newCompanyDialogCancel": "Abbrechen",
    "entry.register.departmentPlaceholder": "Abteilung",
    "entry.register.anonymousToggle": "Anonym teilnehmen",
    "entry.register.anonymousHelper": "Deine E-Mail wird nur für Anmeldung und Kontobestätigung verwendet. Wir teilen sie nicht mit deinem Unternehmen.",
    "entry.register.firstName": "Vorname",
    "entry.register.lastName": "Nachname",
    "entry.register.positionPlaceholder": "Position",
    "entry.register.personalAddress": "Persönliche Adresse",
    "entry.register.loginPrompt": "Du hast schon ein Konto?",
    "entry.register.loginLink": "Du hast schon ein Konto? Hier anmelden",
    "entry.register.loginCta": "Hier anmelden",

    "entry.reset.title": "Passwort zurücksetzen",
    "entry.reset.subtitle": "Gib deine E-Mail ein und wir senden dir einen Reset-Link.",
    "entry.reset.cta": "Reset-Link senden",
    "entry.reset.backToLogin": "Zurück zur Anmeldung",

    "entry.confirm.title": "Bestätigen Sie Ihre E-Mail",
    "entry.confirm.subtitle": "Wir haben Ihnen einen Bestätigungslink gesendet. Bitte überprüfen Sie Ihren Posteingang und bestätigen Sie Ihre E-Mail-Adresse, bevor Sie sich anmelden.",
    "entry.confirm.backToLogin": "Zurück zur Anmeldung",
    "entry.confirm.resend": "Bestätigung erneut senden",

    "entry.loading": "Lädt…",

    // Errors / info
    "entry.err.invalidEmail": "Bitte gib eine gültige Firmen-E-Mail ein.",
    "entry.err.invalidEmailDomain": "Wir konnten die Domain dieser E-Mail nicht verifizieren. Bitte auf Tippfehler prüfen.",
    "entry.err.notFound": "Diese E-Mail wurde in den Firmendaten nicht gefunden.",
    "entry.err.edgeInvoke": "Anfrage an die Edge Function ist fehlgeschlagen.",
    "entry.err.noPassword": "Bitte gib dein Passwort ein.",
    "entry.err.linkFailed": "Anmeldung erfolgreich, aber Verknüpfung ist fehlgeschlagen.",
    "entry.err.pwTooShort": "Das Passwort muss mindestens 8 Zeichen lang sein.",
    "entry.err.companyRequired": "Bitte gib den Firmennamen ein.",
    "entry.err.companyUnconfirmed": "Bitte bestätige dein Unternehmen, bevor du fortfährst.",
    "entry.err.companyNameRequired": "Bitte gib den eingetragenen Firmennamen ein.",
    "entry.err.departmentRequired": "Bitte wähle deine Abteilung aus.",
    "entry.err.nameRequired": "Bitte gib Vor- und Nachnamen ein.",
    "entry.err.userExists": "Diese E-Mail existiert bereits. Bitte melde dich an.",
    "entry.err.selfRegisterFailed": "Konto konnte nicht erstellt werden. Bitte versuche es erneut.",
    "entry.alert.userExists": "Diese E-Mail existiert bereits. Bitte melde dich an.",
    "entry.alert.checkEmail": "Bitte bestätige dein Konto per E-Mail und melde dich danach an.",
    "entry.info.checkEmailConfirm": "Bitte bestätige dein Konto per E-Mail und melde dich danach an.",
    "entry.reset.invalidEmail": "Bitte gib eine gültige E-Mail ein.",
    "entry.reset.sent": "Falls diese E-Mail existiert, wurde ein Reset-Link gesendet. Bitte prüfe deinen Posteingang.",
  },
};

export function translate(lang: Lang, key: string): string {
  return translations[lang][key] ?? translations.en[key] ?? key;
}
